import { arrayMove } from '@dnd-kit/sortable';

import type { IndexDateCriterion, IndexDateResolution } from '@verantos-dev/types-cohorts';

import type {
  AnalyticalVariableWithChangeStatus,
  CohortDefinitionStoreActions,
  CohortDefinitionStoreState,
  Criterion,
  LegacyEntities,
  StudyPeriodState,
} from '../types';

// A criterion is reorderable only when it's an analytical-variable criterion
// with a resolvable id — index-date and legacy `subject_variable` criteria
// return null and are excluded from the reorderable subset entirely (see
// `reorderCriteria` below). Unresolved stub AV criteria (no id yet) also
// return null; reordering is disabled in the UI while stubs are unresolved,
// so this should only be reached defensively.
const getReorderableCriterionKey = (criterion: Criterion): string | null =>
  criterion.criteriaType === 'analytical_variable'
    ? criterion.sourceLibraryAvId ?? criterion.analyticalVariableId ?? null
    : null;

const upsertAV = (
  avs: AnalyticalVariableWithChangeStatus[],
  av: AnalyticalVariableWithChangeStatus
): AnalyticalVariableWithChangeStatus[] => {
  const index = avs.findIndex((v) => v.id === av.id);
  return index >= 0 ? avs.map((v, i) => (i === index ? av : v)) : [...avs, av];
};

type Store = CohortDefinitionStoreState & CohortDefinitionStoreActions;
type SetState = (
  partial: Store | Partial<Store> | ((state: Store) => Store | Partial<Store>)
) => void;
type GetState = () => Store;

export const createAVSlice = (set: SetState, get: GetState) => ({
  analyticalVariables: [] as AnalyticalVariableWithChangeStatus[],
  criteria: [] as Criterion[],
  hasStagedChanges: false,
  validationRequests: {} as Record<string, number>,
  conceptGroupErrorCounts: {} as Record<string, number>,

  setAnalyticalVariable: (av: AnalyticalVariableWithChangeStatus) => {
    set((state) => ({
      analyticalVariables: upsertAV(state.analyticalVariables, av),
      hasStagedChanges: true,
    }));
    get().requestValidation(av.id);
  },

  updateAnalyticalVariable: (id: string, updates: Partial<AnalyticalVariableWithChangeStatus>) => {
    set((state) => {
      const existing = state.analyticalVariables;
      const index = existing.findIndex((v) => v.id === id);
      if (index < 0) return state;
      const updated = existing.map((v, i) =>
        i === index ? { ...v, ...updates, changeStatus: undefined, changedBy: undefined } : v
      );
      return { analyticalVariables: updated, hasStagedChanges: true };
    });
    get().requestValidation(id);
  },

  deleteAnalyticalVariableCriterion: (avId: string) =>
    set((state) => ({
      analyticalVariables: state.analyticalVariables.filter((v) => v.id !== avId),
      criteria: state.criteria.filter(
        (c) => !('analyticalVariableId' in c && c.analyticalVariableId === avId)
      ),
      hasStagedChanges: true,
    })),

  deleteLibraryReferenceCriterion: (sourceLibraryAvId: string) =>
    // Library-reference criteria (PR #3647): match by `sourceLibraryAvId`
    // rather than `analyticalVariableId` (which is null for these). No
    // local AV entry to clean up; the AV lives in the library.
    set((state) => ({
      criteria: state.criteria.filter(
        (c) =>
          !(
            c.criteriaType === 'analytical_variable' &&
            c.analyticalVariableId === null &&
            'sourceLibraryAvId' in c &&
            c.sourceLibraryAvId === sourceLibraryAvId
          )
      ),
      hasStagedChanges: true,
    })),

  deleteIndexDateCriterion: () =>
    set((state) => {
      const indexDateCriterion = state.criteria.find((c) => c.criteriaType === 'index_date');
      // When the index date is backed by an AV, remove that AV too
      const avIdToRemove =
        indexDateCriterion &&
        'value' in indexDateCriterion &&
        indexDateCriterion.value?.type === 'Analytical variable'
          ? indexDateCriterion.value.analyticalVariableId
          : undefined;

      return {
        criteria: state.criteria.filter((c) => c.criteriaType !== 'index_date'),
        analyticalVariables: avIdToRemove
          ? state.analyticalVariables.filter((v) => v.id !== avIdToRemove)
          : state.analyticalVariables,
        indexDate: undefined,
        hasStagedChanges: true,
      };
    }),

  updateIndexDateCriterion: (updatedValue: IndexDateCriterion['value']) =>
    set((state) => ({
      criteria: state.criteria.map((c) =>
        c.criteriaType === 'index_date'
          ? { ...c, value: updatedValue, changeStatus: undefined, changedBy: undefined }
          : c
      ),
      hasStagedChanges: true,
    })),

  updateCriteriaSelection: (avId: string, criteriaSelection: string) =>
    set((state) => ({
      criteria: state.criteria.map((c) =>
        c.criteriaType === 'analytical_variable' &&
        'analyticalVariableId' in c &&
        c.analyticalVariableId === avId
          ? { ...c, criteriaSelection }
          : c
      ),
      hasStagedChanges: true,
    })),

  reorderCriteria: (activeKey: string, overKey: string) =>
    set((state) => {
      const reorderableIndices: number[] = [];
      const reorderableKeys: string[] = [];
      state.criteria.forEach((c, i) => {
        const key = getReorderableCriterionKey(c);
        if (key !== null) {
          reorderableIndices.push(i);
          reorderableKeys.push(key);
        }
      });

      const fromPos = reorderableKeys.indexOf(activeKey);
      const toPos = reorderableKeys.indexOf(overKey);
      if (fromPos < 0 || toPos < 0 || fromPos === toPos) return state;

      // Move only among the reorderable values, then scatter them back into
      // the exact same global slots (in their new order) — every other
      // slot (index date, legacy subject_variable) keeps its value AND
      // position untouched, so the index date can never end up ahead of
      // position 1 as a side effect of this move.
      const reorderedValues = arrayMove(
        reorderableIndices.map((i) => state.criteria[i]),
        fromPos,
        toPos
      );
      const criteria = [...state.criteria];
      reorderableIndices.forEach((globalIndex, pos) => {
        criteria[globalIndex] = reorderedValues[pos];
      });

      return { criteria, hasStagedChanges: true };
    }),

  updateAvCriterionSourceLibraryAvId: (avId: string, sourceLibraryAvId: string | null) =>
    set((state) => ({
      criteria: state.criteria.map((c) => {
        if (
          c.criteriaType === 'analytical_variable' &&
          'analyticalVariableId' in c &&
          c.analyticalVariableId === avId
        ) {
          return { ...c, sourceLibraryAvId };
        }
        if (
          c.criteriaType === 'index_date' &&
          'value' in c &&
          c.value?.type === 'Analytical variable' &&
          c.value.analyticalVariableId === avId
        ) {
          return { ...c, value: { ...c.value, sourceLibraryAvId } };
        }
        return c;
      }),
    })),

  addAnalyticalVariableCriterionFromLibrary: (
    av: AnalyticalVariableWithChangeStatus,
    criteriaSelection: string,
    sourceLibraryAvId: string
  ) => {
    set((state) => {
      const newCriterion = {
        criteriaType: 'analytical_variable' as const,
        usageType: 'criterion' as const,
        analyticalVariableId: av.id,
        criteriaSelection,
        sourceLibraryAvId,
      };
      return {
        analyticalVariables: upsertAV(state.analyticalVariables, av),
        criteria: [...state.criteria, newCriterion],
        hasStagedChanges: true,
      };
    });
    get().requestValidation(av.id);
  },

  addLibraryReferenceCriterion: (sourceLibraryAvId: string, criteriaSelection: string) => {
    // Reference-by-id: criterion carries `sourceLibraryAvId` only, no entry
    // in `analyticalVariables`. The cohort save path serializes the empty
    // local AVs slot; the backend resolver loads the AV from the library at
    // execution time. No `requestValidation` call — there's no local AV to
    // validate.
    set((state) => ({
      criteria: [
        ...state.criteria,
        {
          criteriaType: 'analytical_variable' as const,
          usageType: 'criterion' as const,
          analyticalVariableId: null,
          criteriaSelection,
          sourceLibraryAvId,
        },
      ],
      hasStagedChanges: true,
    }));
  },

  setIndexDateFromLibrary: (
    av: AnalyticalVariableWithChangeStatus,
    dateResolution: IndexDateResolution,
    sourceLibraryAvId: string
  ) => {
    set((state) => {
      const existingIndexDate = state.criteria.find((c) => c.criteriaType === 'index_date');
      const existingAvId =
        existingIndexDate &&
        'value' in existingIndexDate &&
        existingIndexDate.value?.type === 'Analytical variable'
          ? existingIndexDate.value.analyticalVariableId
          : undefined;

      const baseAVs = existingAvId
        ? state.analyticalVariables.filter((v) => v.id !== existingAvId)
        : state.analyticalVariables;

      const newCriterion: IndexDateCriterion = {
        criteriaType: 'index_date',
        value: {
          type: 'Analytical variable',
          analyticalVariableId: av.id,
          dateResolution,
          sourceLibraryAvId,
        },
      };

      return {
        analyticalVariables: upsertAV(baseAVs, av),
        criteria: [...state.criteria.filter((c) => c.criteriaType !== 'index_date'), newCriterion],
        hasStagedChanges: true,
      };
    });
    get().requestValidation(av.id);
  },

  setIndexDateFromLibraryReference: (
    sourceLibraryAvId: string,
    dateResolution: IndexDateResolution
  ) => {
    // Reference-by-id index date. If the previous index date materialized a
    // local AV (legacy clone path), strip that AV from the local array — the
    // new criterion no longer needs it. The runner resolves
    // `sourceLibraryAvId` against the library at execution time.
    set((state) => {
      const existingIndexDate = state.criteria.find((c) => c.criteriaType === 'index_date');
      const existingAvId =
        existingIndexDate &&
        'value' in existingIndexDate &&
        existingIndexDate.value?.type === 'Analytical variable'
          ? existingIndexDate.value.analyticalVariableId
          : undefined;

      const baseAVs = existingAvId
        ? state.analyticalVariables.filter((v) => v.id !== existingAvId)
        : state.analyticalVariables;

      const newCriterion: IndexDateCriterion = {
        criteriaType: 'index_date',
        value: {
          type: 'Analytical variable',
          analyticalVariableId: null,
          dateResolution,
          sourceLibraryAvId,
        },
      };

      return {
        analyticalVariables: baseAVs,
        criteria: [...state.criteria.filter((c) => c.criteriaType !== 'index_date'), newCriterion],
        hasStagedChanges: true,
      };
    });
  },

  setCalendarDateIndexDate: (indexDateValue: string) =>
    set((state) => {
      const existingIndexDate = state.criteria.find((c) => c.criteriaType === 'index_date');
      const existingAvId =
        existingIndexDate &&
        'value' in existingIndexDate &&
        existingIndexDate.value?.type === 'Analytical variable'
          ? existingIndexDate.value.analyticalVariableId
          : undefined;

      const newCriterion: IndexDateCriterion = {
        criteriaType: 'index_date',
        value: { type: 'Calendar date', indexDateValue },
      };

      return {
        analyticalVariables: existingAvId
          ? state.analyticalVariables.filter((v) => v.id !== existingAvId)
          : state.analyticalVariables,
        criteria: [...state.criteria.filter((c) => c.criteriaType !== 'index_date'), newCriterion],
        hasStagedChanges: true,
      };
    }),

  setFromServer: (
    avs: AnalyticalVariableWithChangeStatus[],
    criteria: Criterion[],
    studyPeriod: StudyPeriodState,
    legacyEntities?: LegacyEntities
  ) =>
    set({
      analyticalVariables: avs,
      criteria,
      studyPeriod,
      hasStagedChanges: false,
      ...(legacyEntities && {
        simpleEvents: legacyEntities.simpleEvents,
        derivedEvents: legacyEntities.derivedEvents,
        indexDate: legacyEntities.indexDate,
        subjectVariables: legacyEntities.subjectVariables,
      }),
    }),

  setHasStagedChanges: (hasStagedChanges: boolean) => set({ hasStagedChanges }),

  setAgentRefetchPending: (agentRefetchPending: boolean) => set({ agentRefetchPending }),

  clearChangeStatuses: () =>
    set((state) => ({
      analyticalVariables: state.analyticalVariables.map((av) =>
        av.changeStatus !== undefined || av.changedBy !== undefined
          ? { ...av, changeStatus: undefined, changedBy: undefined }
          : av
      ),
      criteria: state.criteria.map((c) =>
        'changeStatus' in c || 'changedBy' in c
          ? { ...c, changeStatus: undefined, changedBy: undefined }
          : c
      ),
      studyPeriod:
        state.studyPeriod.changeStatus !== undefined || state.studyPeriod.changedBy !== undefined
          ? { ...state.studyPeriod, changeStatus: undefined, changedBy: undefined }
          : state.studyPeriod,
    })),

  requestValidation: (avId: string) =>
    set((state) => ({
      validationRequests: {
        ...state.validationRequests,
        [avId]: (state.validationRequests[avId] ?? 0) + 1,
      },
    })),

  setConceptGroupErrorCounts: (counts: Record<string, number>) =>
    set({ conceptGroupErrorCounts: counts }),
});
